/**
 * Client-side enrichment-queue store.
 *
 * Mirrors the role useSupportNotificationsStore plays for support tickets:
 * keeps an authoritative client view of background enrichment tasks
 * (contact unlock, in the future also company enrichment / scoring)
 * so the Header icon + inline placeholders can render without
 * round-tripping to the backend.
 *
 * Persisted to localStorage with a short TTL so a refresh during an
 * in-flight unlock doesn't lose the row, but stale entries from a
 * previous browser session don't haunt the UI forever.
 */

import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from './useAppStore';

export type EnrichmentEntryType =
  | 'contact_unlock'
  | 'lead_creation'
  | 'news_monitor'
  | 'linkedin_posts'
  | 'linkedin_experience'
  | 'phone_search'
  // Envio LinkedIn fire-and-forget (convite/mensagem via sessão Live):
  // messageId = job_id do scraper; o linkedinJobTracker é o motor de polling
  // e reidrata jobs abertos no boot (o F5 não perde o acompanhamento).
  | 'linkedin_send';
export type EnrichmentStatus = 'processing' | 'completed' | 'failed' | 'no_data';

export interface EnrichmentQueueEntry {
  /** For `contact_unlock`: the enrichmentMessageId returned by
   *  POST /contacts/unlock. For `lead_creation`: the prospect id
   *  (so the detector can poll GET /prospects/{id}). The store dedupes
   *  by this field across both types — id namespaces don't collide
   *  in practice (messageId is a UUID, prospect id is also a UUID). */
  messageId: string;
  type: EnrichmentEntryType;
  /** Human label, e.g. "Alejandra Moreno – Stefanini" (contact) or
   *  "MICROSOFT — Lead criado" (lead). */
  label: string;
  /** For contact_unlock: the deal that owns the contact. For
   *  lead_creation: unused (kept for shape stability — set to ""). */
  dealId: string;
  /** For contact_unlock: the contact_core id. For lead_creation: the
   *  prospect id (mirrored from `messageId` to keep the cell-level
   *  selector `isContactProcessing` working in DealContacts). */
  contactCoreId: string;
  /** Company CNPJ (digits or formatted) when the enqueuing caller knows it.
   *  Lets the detector invalidate the exact `['contacts','by-cnpj',…]`
   *  list instead of every mounted contacts query. Optional — entries
   *  without it fall back to the broad invalidation. */
  cnpj?: string;
  status: EnrichmentStatus;
  startedAt: number;
  completedAt?: number;
  /** Set true once the popover row has been seen. */
  viewed: boolean;
  /** Guards against firing toast+desktop notification more than once. */
  alertEmitted: boolean;
  /** Optional outcome flags surfaced by the polling result. */
  hasEmail?: boolean;
  hasPhone?: boolean;
  /* ── linkedin_send only ─────────────────────────────────────────────── */
  /** Passo da cadência a concluir no desfecho (fallback client do server). */
  cadenceId?: string;
  stepId?: string;
  /** convite ou mensagem — muda copys e rótulos. */
  sendKind?: 'conexao' | 'mensagem';
  /** Texto enviado — preservado para o "Falhou — re-tentar" reabrir com draft. */
  draft?: string;
  /** Desfecho de falha específico (dirige o chip do passo no card).
   *  `rate_limited` é DISTINTO dos demais: não é falha do ENVIO, é a
   *  CONTA sinalizada pelo LinkedIn — nunca convida a reenviar. */
  failKind?: 'not_connected' | 'auth_challenge' | 'failed' | 'lost' | 'rate_limited';
  /** Resultado terminal recebido do job; `completed` sozinho não prova envio. */
  outcome?: string;
  /** Diagnóstico técnico saneado do scraper, quando veio no resultado. */
  failureStep?: string;
  failureHttp?: number;
  /** Escopo local para não expor nem retomar jobs de outra conta/tenant. */
  ownerUserId?: string;
  ownerTenantId?: string;
}

export interface EnrichmentOwnerScope {
  userId: string;
  tenantId: string;
}

function normalizeOwnerId(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value)) {
    return String(value);
  }
  return undefined;
}

export function getCurrentEnrichmentOwnerScope(
  currentUser?: unknown,
): EnrichmentOwnerScope | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const persistedUser = JSON.parse(localStorage.getItem('user') || 'null') as unknown;
    const authenticatedUser =
      currentUser === undefined ? useAppStore.getState().currentUser ?? persistedUser : currentUser;
    const user = authenticatedUser as {
      id?: unknown;
      tenantId?: unknown;
      tenant_id?: unknown;
    } | null;
    const userId = normalizeOwnerId(user?.id);
    const activeTenant = localStorage.getItem('janus.activeTenant');
    const tenantId = activeTenant || normalizeOwnerId(user?.tenantId) || normalizeOwnerId(user?.tenant_id);
    return userId && tenantId ? { userId, tenantId } : undefined;
  } catch {
    return undefined;
  }
}

/** Subscribes to both queue changes and auth changes so account switches hide
 * the previous owner's rows immediately, even before the next queue update. */
export function useEnrichmentQueueEntriesForCurrentOwner(): EnrichmentQueueEntry[] {
  const currentUser = useAppStore((state) => state.currentUser);
  const entries = useEnrichmentQueueStore(useShallow((state) => state.entries));
  const [tenantRevision, setTenantRevision] = useState(0);
  useEffect(() => {
    const onTenantChanged = () => setTenantRevision((revision) => revision + 1);
    window.addEventListener('janus:active-tenant-changed', onTenantChanged);
    window.addEventListener('storage', onTenantChanged);
    return () => {
      window.removeEventListener('janus:active-tenant-changed', onTenantChanged);
      window.removeEventListener('storage', onTenantChanged);
    };
  }, []);
  return useMemo(
    () => selecionarEntradasDoEscopoAtual(entries, getCurrentEnrichmentOwnerScope(currentUser)),
    [entries, currentUser, tenantRevision],
  );
}

export function pertenceAoEscopoDoEnriquecimento(
  entrada: EnrichmentQueueEntry,
  escopo?: EnrichmentOwnerScope,
): boolean {
  const ownerScope = arguments.length > 1 ? escopo : getCurrentEnrichmentOwnerScope();
  return Boolean(
    ownerScope &&
      entrada.ownerUserId === ownerScope.userId &&
      entrada.ownerTenantId === ownerScope.tenantId,
  );
}

export function selecionarEntradasDoEscopoAtual(
  entradas: EnrichmentQueueEntry[],
  escopo?: EnrichmentOwnerScope,
): EnrichmentQueueEntry[] {
  const ownerScope = arguments.length > 1 ? escopo : getCurrentEnrichmentOwnerScope();
  if (!ownerScope) return [];
  return entradas.filter((entrada) => pertenceAoEscopoDoEnriquecimento(entrada, ownerScope));
}

export function selecionarUltimoEnvioLinkedIn(
  entradas: EnrichmentQueueEntry[],
  escopo?: EnrichmentOwnerScope,
): EnrichmentQueueEntry | undefined {
  const scopedEntries =
    arguments.length > 1
      ? selecionarEntradasDoEscopoAtual(entradas, escopo)
      : selecionarEntradasDoEscopoAtual(entradas);
  return scopedEntries
    .filter((entrada) => entrada.type === 'linkedin_send')
    .sort((a, b) => (b.completedAt ?? b.startedAt) - (a.completedAt ?? a.startedAt))[0];
}

interface EnrichmentQueueState {
  entries: EnrichmentQueueEntry[];

  enqueue: (
    entry: Omit<EnrichmentQueueEntry, 'status' | 'viewed' | 'alertEmitted'> &
      Partial<Pick<EnrichmentQueueEntry, 'status' | 'viewed' | 'alertEmitted'>>,
  ) => void;
  markStatus: (
    messageId: string,
    status: EnrichmentStatus,
    extras?: {
      hasEmail?: boolean;
      hasPhone?: boolean;
      failKind?: EnrichmentQueueEntry['failKind'];
      outcome?: string;
      failureStep?: string;
      failureHttp?: number;
    },
  ) => void;
  markAlertEmitted: (messageId: string) => void;
  markAllViewed: () => void;
  removeOnClick: (messageId: string) => void;
  clearOlderThan: (ageMs: number) => void;
  clear: () => void;
}

/** Tasks that have been sitting in the store for longer than this are
 *  GC'd on the next mount. Long enough to survive a refresh in the
 *  middle of an unlock, short enough that a yesterday's run doesn't
 *  reappear at the top of the queue. */
const MAX_ENTRY_AGE_MS = 10 * 60 * 1000;

/** Hard cap on entries kept in the store — bounds re-render cost for
 *  power users that trigger lots of reveals in a session. */
const MAX_ENTRIES = 25;

export const useEnrichmentQueueStore = create<EnrichmentQueueState>()(
  persist(
    (set, get) => ({
      entries: [],

      enqueue: (entry) =>
        set((state) => {
          const ownerScope = getCurrentEnrichmentOwnerScope();
          const existing = state.entries.find(
            (e) =>
              e.messageId === entry.messageId &&
              e.ownerUserId === ownerScope?.userId &&
              e.ownerTenantId === ownerScope?.tenantId,
          );
          if (existing) {
            return state;
          }
          const next: EnrichmentQueueEntry = {
            ...entry,
            status: entry.status ?? 'processing',
            viewed: entry.viewed ?? false,
            alertEmitted: entry.alertEmitted ?? false,
            ownerUserId: ownerScope?.userId,
            ownerTenantId: ownerScope?.tenantId,
          };
          return {
            entries: [next, ...state.entries].slice(0, MAX_ENTRIES),
          };
        }),

      markStatus: (messageId, status, extras) =>
        set((state) => {
          const ownerScope = getCurrentEnrichmentOwnerScope();
          return {
            entries: state.entries.map((e) =>
              e.messageId === messageId && pertenceAoEscopoDoEnriquecimento(e, ownerScope)
                ? {
                    ...e,
                    status,
                    completedAt: status !== 'processing' ? Date.now() : e.completedAt,
                    hasEmail: extras?.hasEmail ?? e.hasEmail,
                    hasPhone: extras?.hasPhone ?? e.hasPhone,
                    failKind: extras?.failKind ?? e.failKind,
                    outcome: extras?.outcome ?? e.outcome,
                    failureStep: extras?.failureStep ?? e.failureStep,
                    failureHttp: extras?.failureHttp ?? e.failureHttp,
                  }
                : e,
            ),
          };
        }),

      markAlertEmitted: (messageId) =>
        set((state) => {
          const ownerScope = getCurrentEnrichmentOwnerScope();
          return {
            entries: state.entries.map((e) =>
              e.messageId === messageId && pertenceAoEscopoDoEnriquecimento(e, ownerScope)
                ? { ...e, alertEmitted: true }
                : e,
            ),
          };
        }),

      markAllViewed: () =>
        set((state) => {
          const ownerScope = getCurrentEnrichmentOwnerScope();
          return {
            entries: state.entries.map((e) =>
              pertenceAoEscopoDoEnriquecimento(e, ownerScope) && !e.viewed
                ? { ...e, viewed: true }
                : e,
            ),
          };
        }),

      removeOnClick: (messageId) =>
        set((state) => {
          const ownerScope = getCurrentEnrichmentOwnerScope();
          return {
            entries: state.entries.filter(
              (e) => e.messageId !== messageId || !pertenceAoEscopoDoEnriquecimento(e, ownerScope),
            ),
          };
        }),

      clearOlderThan: (ageMs) => {
        const now = Date.now();
        set((state) => ({
          entries: state.entries.filter((e) => {
            const ts = e.completedAt ?? e.startedAt;
            return now - ts < ageMs;
          }),
        }));
        // Compact persist
        void get();
      },

      clear: () => set({ entries: [] }),
    }),
    {
      name: 'enrichment-queue-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries }),
      version: 1,
      onRehydrateStorage: () => (state) => {
        // Drop anything older than MAX_ENTRY_AGE_MS or stuck in
        // processing forever (likely server crash between enqueue
        // and completion).
        if (!state) return;
        const now = Date.now();
        state.entries = state.entries.filter((e) => {
          const ts = e.completedAt ?? e.startedAt;
          if (now - ts > MAX_ENTRY_AGE_MS) return false;
          if (e.status === 'processing' && now - e.startedAt > MAX_ENTRY_AGE_MS) {
            return false;
          }
          return true;
        });
      },
    },
  ),
);

/* ── Derived selectors ──────────────────────────────────────────────── */

export const selectPendingCount = (s: EnrichmentQueueState): number =>
  selecionarEntradasDoEscopoAtual(s.entries).reduce(
    (acc, e) => (e.status === 'processing' ? acc + 1 : acc),
    0,
  );

export const selectUnviewedCompleted = (s: EnrichmentQueueState): number =>
  selecionarEntradasDoEscopoAtual(s.entries).reduce(
    (acc, e) => (e.status !== 'processing' && !e.viewed ? acc + 1 : acc),
    0,
  );

export const selectTotalBadge = (s: EnrichmentQueueState): number =>
  selectPendingCount(s) + selectUnviewedCompleted(s);

/** Entrada de envio LinkedIn (viva ou resolvida) para um passo de cadência —
 *  dirige o chip de estado no card (Enviando… / Falhou / Aguardando aceite). */
export const selectLinkedinSendForStep = (
  entries: EnrichmentQueueEntry[],
  stepId: string,
): EnrichmentQueueEntry | undefined =>
  entries.find((e) => e.type === 'linkedin_send' && e.stepId === stepId);

/** True iff at least one entry is processing — drives the inline
 *  AI Enriching badge on the contact row. */
export const isContactProcessing = (
  entries: EnrichmentQueueEntry[],
  contactCoreId: string,
): boolean =>
  entries.some(
    (e) =>
      e.contactCoreId === contactCoreId &&
      e.status === 'processing' &&
      (e.type === 'contact_unlock' || e.type === 'phone_search'),
  );
